/**
 * @fileoverview サインインページコンポーネント
 *
 * このファイルでは、ユーザー認証のためのサインインページを定義しています。
 * ユーザー名/パスワードによるログインフォームや、外部認証サービスへの
 * リダイレクト機能を提供します。
 */

/**
 * サインインページコンポーネント
 *
 * ユーザーがシステムにサインインするためのインターフェースを提供します。
 * 認証情報の入力フォーム、エラーメッセージの表示、認証プロセスの開始機能などを
 * 含みます。複数の認証方式に対応し、ユーザビリティとセキュリティのバランスを
 * 考慮した設計になっています。
 *
 * @returns {JSX.Element} サインインフォーム
 */

import { Stack, Text, TextInput, PasswordInput } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { isEmail, useForm } from "@mantine/form";

import { useLocation } from "react-router";
import { Link } from "@/router";
import { Card } from "@/components/card";
import { Button } from "@/components/button";

export default function SigninPage() {
  const search = useLocation().search;
  const queries = new URLSearchParams(search);

  const ticketId = queries.get("ticket_session_id");
  if (!ticketId) {
    console.error("Ticket is not found.");
    return;
  }

  const form = useForm({
    mode: "uncontrolled",
    initialValues: { email: "", password: "" },
    validate: {
      email: isEmail("メールアドレスの形式が正しくありません。"),
    },
    validateInputOnBlur: true,
  });

  const [loading, { open: setLoading }] = useDisclosure(false);

  const bffOrigin = import.meta.env.VITE_BFF_ORIGIN;
  const loginUrl = `${bffOrigin}/v1/authenticate`;

  return (
    <Card
      title="サインイン"
      description="メールアドレスとパスワードを入力してください。"
    >
      <form action={loginUrl} method="POST" onSubmit={() => setLoading()}>
        <Stack className="gap-10">
          <div className="flex flex-col gap-5">
            <TextInput
              name="email"
              key={form.key("email")}
              {...form.getInputProps("email")}
              label="メールアドレス"
              placeholder="email"
              required={true}
            />
            <PasswordInput
              name="password"
              key={form.key("password")}
              {...form.getInputProps("password")}
              label="パスワード"
              placeholder="password"
              required={true}
            />
            <input type="hidden" name="ticket_session_id" value={ticketId} />
          </div>
          <div className="flex justify-between">
            <div className="flex content-center">
              <Link to="/reset-password">
                <Text className="text-blue-600 hover:text-blue-700">
                  パスワードを忘れた場合
                </Text>
              </Link>
            </div>
            <Button
              text="サインイン"
              type="submit"
              disabled={loading}
              loading={loading}
            />
          </div>
        </Stack>
      </form>
    </Card>
  );
}
